/**
 * EL CATÁLOGO DEL CONTADOR — entenderlo ANTES de crear la empresa, y completarlo DESPUÉS.
 *
 * 🔴 EL PROBLEMA, MEDIDO CON UN CATÁLOGO REAL DE 239 CUENTAS:
 *
 * Todo el producto identifica cuentas por CÓDIGO. Sobre el catálogo de otro contador eso es
 * una pregunta sin sentido: de esos 239 códigos, 72 coincidían con los nuestros y **54
 * significaban otra cosa**. El `410101` que para nosotros es «Ventas gravadas» era
 * «Materiales Directos». Sin nada que lo frenara, la empresa arrancaba con el Estado de
 * Resultados invertido y en silencio.
 *
 * 🔴 Y EL REDISEÑO, MEDIDO CON EL PRIMER ASESOR REAL (14-sep-2026):
 *
 * La pantalla anterior hacía las preguntas correctas en una sola tarjeta larga: el autorizante,
 * el archivo, 13 grupos con rótulos de 87 caracteres y 105 conceptos con un desplegable cada
 * uno, con «lo que falta» al final. El asesor se rindió a los 65 segundos con 3 de 13 grupos y
 * 0 conceptos, y nunca creó la empresa. Lo que cambia:
 *
 *   - PASOS con avance, y arriba siempre qué falta.
 *   - Solo se REVISA lo dudoso: lo que el analizador reconoce se confirma de un clic.
 *   - Solo los 24 conceptos que FRENAN la creación, con su nombre; el resto, para después.
 *   - Una VISTA PREVIA de qué entra y qué queda fuera.
 *   - Con la empresa ya creada, COMPLETARLA en vez de no poder hacer nada.
 *   - La firma del catálogo aparte: no frena la importación, solo su cierre.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CheckCircle2, Upload, AlertTriangle, Loader2, Pencil, Search, Link2 } from 'lucide-react'
import Stepper from '@/components/common/Stepper'
import ElegirCuentaCatalogo from './ElegirCuentaCatalogo'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import cd2bPropuestaService from '@/services/cd2bPropuestaService'
import { mensajeDeError } from '@/lib/errores'

/**
 * Qué puede ser un grupo de la numeración. Las de orden se eligen con su naturaleza: una cuenta
 * de orden sin ella sale con el saldo cambiado de signo. Tiene que coincidir con `DecisionGrupo`
 * (gruposDelCatalogo.ts).
 */
const TIPOS = [
  { valor: 'activo', etiqueta: 'Activo' },
  { valor: 'pasivo', etiqueta: 'Pasivo' },
  { valor: 'patrimonio', etiqueta: 'Patrimonio' },
  { valor: 'ingreso', etiqueta: 'Ingresos' },
  { valor: 'costo', etiqueta: 'Costos' },
  { valor: 'gasto', etiqueta: 'Gastos' },
  { valor: 'liquidacion', etiqueta: 'Cuenta liquidadora (Pérdidas y Ganancias)' },
  { valor: 'orden_deudora', etiqueta: 'Cuentas de orden deudoras' },
  { valor: 'orden_acreedora', etiqueta: 'Cuentas de orden acreedoras (por contra)' },
  { valor: 'excluir', etiqueta: 'No importar este grupo' },
]

const etiquetaTipo = (v) => TIPOS.find((t) => t.valor === v)?.etiqueta || v

const SIN_ELEGIR = '__sin__'

const PASOS = [
  { clave: 'archivo', titulo: 'Su catálogo', descripcion: 'Subir el archivo del contador' },
  { clave: 'numeracion', titulo: 'Su numeración', descripcion: 'Qué es cada grupo' },
  { clave: 'cuentas', titulo: 'Cuentas clave', descripcion: 'Caja, bancos, ventas…' },
  { clave: 'revisar', titulo: 'Revisar', descripcion: 'Qué entra y qué no' },
]

/**
 * El rótulo de un grupo, sin la ruta de sus padres. «CUENTAS DE RESULTADO DEUDORAS → COSTOS Y
 * GASTOS DE OPERACIÓN → GASTOS DE VENTA» no cabe en una fila; la ruta completa va en el title.
 */
const rotuloCorto = (nombre) => String(nombre || '').split('→').pop().trim()

/**
 * Un grupo es DUDOSO si nadie propuso nada o si la propuesta no sale de su nombre ni de sus
 * subgrupos. Esos son los que tiene que mirar una persona; el resto se confirma en bloque.
 */
const esDudoso = (g) => !g.propuesto || g.confianza === 'ninguna'

/** Los tres datos del autorizante, vacíos. Los tres o ninguno: un trío a medias no acredita nada. */
const CONTADOR_VACIO = { nombre: '', inscripcionCVPCPA: '', fechaAutorizacion: '' }

/**
 * 🔴 QUIÉN AUTORIZÓ ESTE CATÁLOGO — y por qué son los tres o ninguno.
 *
 * Sin los tres, el catálogo se carga igual pero sin constancia de quién lo autorizó ni cuándo, y
 * no se puede CERRAR. No frena la importación: por eso va en su propia tarjeta y no mezclado con
 * los pasos, que es donde confundía al asesor sobre qué le faltaba para crear la empresa.
 */
function CamposContador({ contador, onCambio, completo, pie = null }) {
  const set = (k) => (e) => onCambio({ ...contador, [k]: e.target.value })
  return (
    <div className="space-y-2">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="cc-nombre">Contador</Label>
          <Input id="cc-nombre" value={contador.nombre} onChange={set('nombre')} placeholder="Nombre completo" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="cc-insc">Inscripción CVPCPA</Label>
          <Input
            id="cc-insc"
            value={contador.inscripcionCVPCPA}
            onChange={set('inscripcionCVPCPA')}
            placeholder="N.º de inscripción"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="cc-fecha">Fecha de autorización</Label>
          <Input id="cc-fecha" type="date" value={contador.fechaAutorizacion} onChange={set('fechaAutorizacion')} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {pie}
        {!completo && (
          <span className="text-body-sm text-warning-700 dark:text-warning-300">
            Faltan los tres: el catálogo entraría sin constancia de quién lo autorizó.
          </span>
        )}
      </div>
    </div>
  )
}

/** Una cifra con su rótulo, para la vista previa. */
function Cifra({ valor, rotulo, tono = 'neutral' }) {
  const color =
    tono === 'bien'
      ? 'text-success-700 dark:text-success-300'
      : tono === 'aviso'
        ? 'text-warning-700 dark:text-warning-300'
        : tono === 'mal'
          ? 'text-danger-700 dark:text-danger-300'
          : 'text-neutral-900 dark:text-neutral-50'
  return (
    <div className="min-w-[7rem] rounded-md border border-neutral-200 dark:border-neutral-800 px-3 py-2">
      <p className={`text-h3 font-semibold tabular-nums ${color}`}>{valor}</p>
      <p className="text-caption text-neutral-500 dark:text-neutral-400">{rotulo}</p>
    </div>
  )
}

/** Una lista corta de cuentas con motivo, plegada. */
function ListaCuentas({ titulo, cuentas }) {
  if (!cuentas?.length) return null
  return (
    <details className="rounded-md border border-neutral-200 dark:border-neutral-800 px-3 py-2">
      <summary className="cursor-pointer text-body-sm font-medium text-neutral-900 dark:text-neutral-50">
        {titulo} ({cuentas.length})
      </summary>
      <ul className="mt-2 max-h-60 space-y-1 overflow-y-auto text-body-sm">
        {cuentas.map((c) => (
          <li key={c.codigo} className="flex flex-wrap gap-x-2">
            <span className="font-mono">{c.codigo}</span>
            <span className="text-neutral-700 dark:text-neutral-300">{c.nombre}</span>
            {c.motivos?.length > 0 && (
              <span className="text-neutral-500 dark:text-neutral-400">— {c.motivos.join(' · ')}</span>
            )}
          </li>
        ))}
      </ul>
    </details>
  )
}

/**
 * Con la empresa creada, el catálogo ya no se cambia aquí: el api rechaza otra declaración y otro
 * archivo (mezclaría dos planes de cuentas). Se dice dónde se hace, en vez de ofrecer un botón
 * que va a fallar.
 */
const CAMBIO_EN_ESCRITORIO =
  'Para cambiarlo, desde la empresa en el escritorio: «Cambiar mi catálogo» → «Reemplazar por completo», que mueve la historia y los ajustes juntos.'

/**
 * Cómo quedó el catálogo EN LA EMPRESA: aplicado y cerrado, aplicado y abierto (y por qué), o sin
 * entrar. Antes solo se pintaba cuando la carga fallaba: un catálogo abierto por falta de firma o
 * de cuenta puente no se veía en ningún sitio.
 */
function EstadoEnLaEmpresa({ carga }) {
  if (!carga) {
    return (
      <Alert>
        <AlertDescription>
          El catálogo todavía no está en la empresa: el alta se interrumpió antes de cargarlo. Se carga al volver a
          pulsar «Crear empresa demo» o al entregarla; si falla, la entrega se detiene.
        </AlertDescription>
      </Alert>
    )
  }
  if (carga.ok === false) {
    return (
      <Alert variant="destructive">
        <AlertDescription className="space-y-1">
          <span className="flex items-center gap-1.5 font-medium">
            <AlertTriangle size={15} /> El catálogo del contador no llegó entero a la empresa
          </span>
          {carga.error && <span className="block">{carga.error}</span>}
          <span className="block">
            No se puede entregar hasta que entre. Usa «Comparar el catálogo con la empresa» para completarlo.
          </span>
        </AlertDescription>
      </Alert>
    )
  }
  return (
    <div className="space-y-2">
      {carga.catalogoCerrado ? (
        <p className="text-body-sm text-success-700 dark:text-success-300 flex items-center gap-1.5">
          <CheckCircle2 size={15} /> Está en la empresa y cerrado como su sistema contable autorizado.
        </p>
      ) : (
        <Alert variant="warning">
          <AlertDescription>
            <span className="font-medium">Está en la empresa, pero abierto</span> (no quedó cerrado como su sistema
            contable autorizado). {carga.avisoCierre || 'Falta la firma del contador.'}
          </AlertDescription>
        </Alert>
      )}
      {carga.avisoMapeo && (
        <Alert variant="warning">
          <AlertDescription>Las cuentas clave no se guardaron en la empresa: {carga.avisoMapeo}</AlertDescription>
        </Alert>
      )}
    </div>
  )
}

const fechaCorta = (d) =>
  d ? new Date(d).toLocaleDateString('es-419', { day: 'numeric', month: 'long', year: 'numeric' }) : ''

/**
 * El enlace para el CONTADOR. Quien sabe qué es cada grupo de la numeración es él; sin enlace el
 * asesor tenía que adivinarlo, y adivinando fue como se rindió el primero.
 */
function EnlaceContador({ propuestaId, enlace, onEmitido }) {
  const [url, setUrl] = useState(null)
  const [trabajando, setTrabajando] = useState(false)
  const [confirmarRenovar, setConfirmarRenovar] = useState(false)
  const vigente = enlace?.estado === 'valido'

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto)
      toast.success('Enlace copiado. Mandáselo al contador.')
    } catch {
      toast('Copialo desde el recuadro.')
    }
  }

  const emitir = async (renovar) => {
    setTrabajando(true)
    try {
      const r = await cd2bPropuestaService.emitirEnlaceCatalogoContador(propuestaId, { renovar })
      setUrl(r.data.url)
      await copiar(r.data.url)
      onEmitido?.()
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo crear el enlace'))
    } finally {
      setTrabajando(false)
      setConfirmarRenovar(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-body font-semibold flex items-center gap-2">
          Enlace para el contador
          {vigente ? (
            <Badge variant="secondary">vigente</Badge>
          ) : enlace ? (
            <Badge variant="outline">vencido</Badge>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
          El contador del cliente puede subir el archivo, decir qué es cada grupo, elegir las cuentas clave y firmar,
          sin cuenta en el portal. Lo que conteste aparece aquí. Crear la empresa sigue siendo tuyo.
        </p>
        {vigente && (
          <p className="text-caption text-neutral-500 dark:text-neutral-400">
            Vence el {fechaCorta(enlace.expiraAt)}.{' '}
            {enlace.ultimoAccesoAt
              ? `Lo abrió por última vez el ${fechaCorta(enlace.ultimoAccesoAt)}.`
              : 'Todavía no lo ha abierto.'}
          </p>
        )}
        {url && <Input readOnly value={url} onFocus={(e) => e.target.select()} aria-label="Enlace para el contador" />}
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" disabled={trabajando} onClick={() => emitir(false)}>
            {trabajando ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Link2 size={14} className="mr-1.5" />}
            {vigente ? 'Copiar el enlace' : 'Crear el enlace'}
          </Button>
          {vigente && (
            <Button size="sm" variant="ghost" disabled={trabajando} onClick={() => setConfirmarRenovar(true)}>
              Cambiar el enlace
            </Button>
          )}
        </div>
      </CardContent>
      <ConfirmDialog
        abierto={confirmarRenovar}
        onCerrar={() => setConfirmarRenovar(false)}
        onConfirmar={() => emitir(true)}
        titulo="¿Cambiar el enlace?"
        descripcion="El enlace anterior deja de abrir. Lo que el contador ya contestó se queda guardado."
        textoConfirmar="Cambiar el enlace"
        variante="destructiva"
        cargando={trabajando}
      />
    </Card>
  )
}

/**
 * @param {string|null} propuestaId  Sin id la cotización TODAVÍA NO EXISTE: modo creación. En modo
 *   público es el token del enlace.
 * @param {Function}    onDeclaracionCambia  Sube la respuesta al formulario, que la guarda con
 *   el borrador. Solo se usa en modo creación — después, cada cambio se persiste al vuelo.
 * @param {object}      servicio  Quién habla con el api: el del asesor o el del enlace del
 *   contador. Mismos métodos, así la pantalla es una sola.
 * @param {boolean}     publico  Lo abre el contador: sin completar la empresa ni el enlace.
 */
export default function CatalogoContador({
  propuestaId,
  onListoCambia,
  onDeclaracionCambia,
  servicio = cd2bPropuestaService,
  publico = false,
}) {
  // 🔴 SIN COTIZACIÓN NO HAY NADA QUE LEER, PERO SÍ QUE PREGUNTAR. El asesor que cotiza directo
  // tiene que ver la pregunta al crearla, no toparse con el freno en «Crear empresa».
  const enCreacion = !propuestaId

  const [cargando, setCargando] = useState(!enCreacion)
  const [subiendo, setSubiendo] = useState(false)
  const [estado, setEstado] = useState(null)
  const [conciliacion, setConciliacion] = useState(null)
  const [repreguntando, setRepreguntando] = useState(false)
  const [contador, setContador] = useState(CONTADOR_VACIO)
  const [propioLocal, setPropioLocal] = useState(null)
  const [archivoPendiente, setArchivoPendiente] = useState(null)
  const [guardandoContador, setGuardandoContador] = useState(false)
  const [paso, setPaso] = useState(null)
  const [verReconocidos, setVerReconocidos] = useState(false)
  const [busqueda, setBusqueda] = useState('')
  const [confirmarReemplazo, setConfirmarReemplazo] = useState(false)
  const [completado, setCompletado] = useState(null)
  const [completando, setCompletando] = useState(false)
  const [confirmarEscribir, setConfirmarEscribir] = useState(false)
  const inputArchivo = useRef(null)

  const decisiones = useMemo(() => estado?.decisiones || {}, [estado?.decisiones])
  const confirmaciones = useMemo(() => estado?.confirmaciones || {}, [estado?.confirmaciones])
  const grupos = useMemo(() => estado?.grupos || [], [estado?.grupos])

  const refrescar = useCallback(async () => {
    if (enCreacion) return null
    try {
      const r = await servicio.estadoCatalogoContador(propuestaId)
      setEstado(r.data)
      // 🔴 SE REPINTA LO CAPTURADO, NO SOLO EL TRÍO COMPLETO: `contador` viene `null` mientras
      // falte uno de los tres, y repintar de ahí vaciaba los campos a quien había tecleado dos.
      const cap = r.data?.contadorCapturado || r.data?.contador
      if (cap) setContador({ ...CONTADOR_VACIO, ...cap })
      return r.data
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo leer el estado del catálogo'))
      return null
    } finally {
      setCargando(false)
    }
  }, [propuestaId, enCreacion, servicio])

  useEffect(() => {
    refrescar()
  }, [refrescar])

  // El botón «Crear empresa» vive en la pantalla de arriba. El freno de verdad está en el api
  // (`exigirCatalogoEntendido`); esto solo evita que el asesor pulse un botón que va a fallar.
  useEffect(() => {
    if (estado) {
      onListoCambia?.({
        propio: !!estado.propio,
        origen: estado.origen || 'sin_declarar',
        listo: !!estado.listo,
        faltan: estado.faltan || [],
      })
    }
  }, [estado, onListoCambia])

  const conciliar = useCallback(
    async (cambios) => {
      try {
        const r = await servicio.conciliarCatalogoContador(propuestaId, cambios)
        setConciliacion(r.data)
        setEstado((prev) => ({
          ...(prev || {}),
          decisiones: r.data.decisiones,
          confirmaciones: r.data.confirmaciones,
          listo: r.data.listo,
          faltan: r.data.faltan,
        }))
        // Cambió lo que entra: la vista previa contra la empresa ya no vale.
        setCompletado(null)
      } catch (e) {
        toast.error(mensajeDeError(e, 'No se pudo guardar'))
      }
    },
    [propuestaId, servicio],
  )

  useEffect(() => {
    if (estado?.cuentas > 0 && !conciliacion) conciliar({})
  }, [estado?.cuentas, conciliacion, conciliar])

  // ── Lo que falta, por paso ──────────────────────────────────────────────────────────────
  const gruposPendientes = useMemo(() => grupos.filter((g) => !decisiones[g.codigo]), [grupos, decisiones])
  const dudosos = useMemo(() => gruposPendientes.filter(esDudoso), [gruposPendientes])
  const reconocidosSinConfirmar = useMemo(
    () => gruposPendientes.filter((g) => !esDudoso(g)),
    [gruposPendientes],
  )

  const clave = useMemo(
    () => (conciliacion?.emparejamientos || []).filter((e) => e.imprescindible),
    [conciliacion],
  )
  const otros = useMemo(
    () => (conciliacion?.emparejamientos || []).filter((e) => !e.imprescindible),
    [conciliacion],
  )
  const clavesSinConfirmar = useMemo(() => clave.filter((e) => !confirmaciones[e.concepto]), [clave, confirmaciones])
  /**
   * 🔴 LA PROPUESTA DE UN CONCEPTO NO SE GUARDABA. El desplegable enseñaba la cuenta propuesta
   * como si fuera la elegida y Radix solo avisa cuando el valor CAMBIA: elegir la que ya decía no
   * guardaba nada, y el freno seguía pidiendo ese concepto. Es el mismo defecto que tenían los
   * grupos. Ahora el valor es lo guardado y las propuestas se aceptan con un botón.
   */
  const propuestasDeClave = useMemo(
    () => clavesSinConfirmar.filter((e) => e.marcada && e.codigo),
    [clavesSinConfirmar],
  )

  const pasoPendiente = !estado?.cuentas
    ? 'archivo'
    : gruposPendientes.length > 0
      ? 'numeracion'
      : clavesSinConfirmar.length > 0
        ? 'cuentas'
        : 'revisar'
  const pasoVisible = paso || pasoPendiente
  const hechos = { archivo: estado?.cuentas > 0, numeracion: gruposPendientes.length === 0, cuentas: clavesSinConfirmar.length === 0 }

  const subir = async (archivo) => {
    if (!archivo) return
    setSubiendo(true)
    try {
      const r = await servicio.analizarCatalogoContador(propuestaId, { archivo })
      const d = r.data
      toast.success(`Se leyeron ${d.cuentas} cuentas de «${d.archivo}».`)
      ;(d.avisos || []).forEach((a) => toast(a, { icon: '⚠️' }))
      setConciliacion(null)
      setCompletado(null)
      setPaso(null)
      await refrescar()
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo leer el catálogo'))
    } finally {
      setSubiendo(false)
      if (inputArchivo.current) inputArchivo.current.value = ''
    }
  }

  // Subir otro archivo borra lo confirmado sobre el anterior (a propósito: si no, la empresa se
  // crearía con decisiones de otro catálogo). Antes lo hacía sin avisar.
  const hayTrabajo = Object.keys(decisiones).length > 0 || Object.keys(confirmaciones).length > 0
  const pedirArchivo = () => (hayTrabajo ? setConfirmarReemplazo(true) : inputArchivo.current?.click())

  const declarar = useCallback(
    async (propio, datosDelContador) => {
      if (enCreacion) {
        setPropioLocal(propio)
        setRepreguntando(false)
        onDeclaracionCambia?.({
          propio,
          contador: propio ? datosDelContador : null,
          archivo: propio ? archivoPendiente : null,
        })
        if (!propio) setArchivoPendiente(null)
        return
      }
      setGuardandoContador(true)
      try {
        const r = await servicio.declararCatalogoContador(propuestaId, { propio, contador: datosDelContador })
        // Con la empresa ya creada, la firma CIERRA el catálogo (o dice por qué no pudo).
        const cierre = r?.data?.cierre
        if (cierre?.catalogoCerrado) toast.success('Firma guardada: el catálogo quedó cerrado como el sistema contable autorizado.')
        else if (cierre?.avisoCierre) toast(`Firma guardada, pero el catálogo sigue abierto: ${cierre.avisoCierre}`, { icon: '⚠️' })
        setRepreguntando(false)
        await refrescar()
      } catch (e) {
        toast.error(mensajeDeError(e, 'No se pudo guardar la respuesta'))
      } finally {
        setGuardandoContador(false)
      }
    },
    [propuestaId, refrescar, enCreacion, onDeclaracionCambia, archivoPendiente, servicio],
  )

  const ensayarEmpresa = async () => {
    setCompletando(true)
    try {
      const r = await cd2bPropuestaService.completarCatalogoContador(propuestaId, { ensayo: true })
      setCompletado(r.data)
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo comparar el catálogo con la empresa'))
    } finally {
      setCompletando(false)
    }
  }

  const escribirFaltantes = async () => {
    setCompletando(true)
    try {
      const r = await cd2bPropuestaService.completarCatalogoContador(propuestaId, { ensayo: false })
      if (r.data.resumen.escritas > 0) toast.success(`Se agregaron ${r.data.resumen.escritas} cuentas a la empresa.`)
      if (r.data.catalogoCerrado) toast.success('El catálogo quedó cerrado como el sistema contable autorizado.')
      else if (r.data.avisoCierre) toast(`El catálogo sigue abierto: ${r.data.avisoCierre}`, { icon: '⚠️' })
      if (r.data.avisoMapeo) toast(`Las cuentas clave no se guardaron: ${r.data.avisoMapeo}`, { icon: '⚠️' })
      setConfirmarEscribir(false)
      await refrescar()
      await ensayarEmpresa()
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudieron agregar las cuentas'))
    } finally {
      setCompletando(false)
    }
  }

  if (cargando) {
    return <p className="text-body-sm text-neutral-500 dark:text-neutral-400 py-4">Cargando…</p>
  }

  // 🔴 Con la empresa creada, la respuesta ya decidió con qué plan nació: ni se repregunta ni se
  // cambia aquí (el api lo rechaza). Se dice dónde se hace.
  const congelado = !enCreacion && !!estado?.empresaCreada
  if (congelado && !estado?.propio) {
    return (
      <p className="text-body-sm text-neutral-500 dark:text-neutral-400">
        Este cliente arrancó con el catálogo de cuentas estándar, y su empresa ya está creada con él. {CAMBIO_EN_ESCRITORIO}
      </p>
    )
  }

  // ── La pregunta, cuando nadie la ha hecho ────────────────────────────────────────────────
  const sinContestar = enCreacion ? propioLocal === null : estado?.origen === 'sin_declarar'
  const elegido = enCreacion ? propioLocal : estado?.origen === 'cotizacion' ? estado.propio : null
  if (!congelado && (sinContestar || repreguntando)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-h3">Catálogo de cuentas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
            ¿Este cliente ya lleva su contabilidad con un catálogo de cuentas propio, el que su
            contador tiene autorizado? Si es así, la empresa <strong>nace con el suyo</strong> y no
            con el estándar.
          </p>
          {/* Las dos opciones pesan igual mientras nadie conteste: con pesos distintos, la
              pregunta se lee como ya contestada. */}
          <div className="flex flex-wrap gap-3">
            <Button
              variant={elegido === true ? 'default' : 'outline'}
              disabled={guardandoContador}
              onClick={() => declarar(true, contador)}
            >
              Sí, trae el catálogo de su contador
            </Button>
            <Button
              variant={elegido === false ? 'default' : 'outline'}
              disabled={guardandoContador}
              onClick={() => declarar(false)}
            >
              No, arranca con el catálogo estándar
            </Button>
          </div>
          <p className="text-body-sm text-neutral-500 dark:text-neutral-400">
            {enCreacion
              ? 'Hay que contestarla para crear el borrador. Se puede cambiar después, mientras la empresa no esté creada.'
              : 'Se puede cambiar mientras la empresa no esté creada.'}
          </p>
        </CardContent>
      </Card>
    )
  }

  if (enCreacion ? propioLocal === false : !estado?.propio) {
    if (!enCreacion && estado?.origen !== 'cotizacion') return null
    return (
      <p className="text-body-sm text-neutral-500 dark:text-neutral-400 flex flex-wrap items-center gap-2">
        Este cliente arranca con el catálogo de cuentas estándar.
        <Button variant="ghost" size="sm" onClick={() => setRepreguntando(true)}>
          <Pencil size={14} className="mr-1.5" /> Trae el de su contador
        </Button>
      </p>
    )
  }

  // ── Contestada que SÍ, pero la cotización aún no existe ─────────────────────────────────
  // El archivo se ELIGE ya (el asesor lo tiene delante) y se sube solo al guardar el borrador.
  if (enCreacion) {
    const completo = Boolean(contador.nombre && contador.inscripcionCVPCPA && contador.fechaAutorizacion)
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-h3">Catálogo de cuentas del contador</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
            Elegí su archivo ya: se sube solo al guardar el borrador, y después se revisa en cuatro
            pasos cortos.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={inputArchivo}
              type="file"
              accept=".xlsx,.xlsm,.xls,.csv,.txt,.json,.dbf"
              className="hidden"
              onChange={(ev) => {
                const f = ev.target.files?.[0] || null
                setArchivoPendiente(f)
                onDeclaracionCambia?.({ propio: true, contador, archivo: f })
              }}
            />
            <Button variant="outline" onClick={() => inputArchivo.current?.click()}>
              <Upload size={16} className="mr-1.5" />
              {archivoPendiente ? 'Elegir otro archivo' : 'Elegir el catálogo (Excel o CSV)'}
            </Button>
            {archivoPendiente ? (
              <span className="text-body-sm text-neutral-600 dark:text-neutral-400">
                <strong>{archivoPendiente.name}</strong> — se sube al guardar
              </span>
            ) : (
              <span className="text-body-sm text-warning-700 dark:text-warning-300">
                Hace falta para crear el borrador.
              </span>
            )}
          </div>
          <div className="space-y-2">
            <h4 className="text-body font-semibold text-neutral-900 dark:text-neutral-50">
              Quién autorizó este catálogo <span className="font-normal text-neutral-500">(se puede dejar para después)</span>
            </h4>
            <CamposContador
              contador={contador}
              onCambio={(c) => {
                setContador(c)
                onDeclaracionCambia?.({ propio: true, contador: c, archivo: archivoPendiente })
              }}
              completo={completo}
            />
          </div>
          <p className="text-body-sm text-neutral-500 dark:text-neutral-400 flex flex-wrap items-center gap-2">
            ¿Me equivoqué?
            <Button variant="ghost" size="sm" onClick={() => setRepreguntando(true)}>
              <Pencil size={14} className="mr-1.5" /> Volver a la pregunta
            </Button>
          </p>
        </CardContent>
      </Card>
    )
  }

  // ── El asistente por pasos ──────────────────────────────────────────────────────────────
  const resumen = conciliacion?.resumen
  const coincide = (e) => {
    const q = busqueda.trim().toLowerCase()
    if (!q) return true
    return (
      e.etiqueta?.toLowerCase().includes(q) ||
      String(e.codigo || '').includes(q) ||
      (e.candidatos || []).some((c) => c.codigo.includes(q) || c.nombre.toLowerCase().includes(q))
    )
  }

  const filaConcepto = (f) => (
    <tr key={f.concepto} className="border-t border-neutral-200 dark:border-neutral-800 align-top">
      <td className="py-2 pr-3 w-40 sm:w-56">
        <p className="text-neutral-900 dark:text-neutral-50">{f.etiqueta}</p>
        {f.ayuda && <p className="text-caption text-neutral-500 dark:text-neutral-400">{f.ayuda}</p>}
      </td>
      <td className="py-2 pr-3">
        {/* El valor es lo GUARDADO. La propuesta se nombra al lado y se acepta con el botón. */}
        <ElegirCuentaCatalogo
          etiqueta={f.etiqueta}
          valor={confirmaciones[f.concepto] || null}
          candidatos={f.candidatos}
          plan={conciliacion?.plan}
          tipos={f.tipos}
          onElegir={(v) => conciliar({ confirmaciones: { ...confirmaciones, [f.concepto]: v || undefined } })}
        />
        {!confirmaciones[f.concepto] && f.codigo && (
          <p className="mt-1 text-caption text-neutral-500 dark:text-neutral-400">
            Se propone {f.codigo} «{f.nombre}».
          </p>
        )}
      </td>
    </tr>
  )

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-h3 flex flex-wrap items-center gap-2">
            Catálogo de cuentas del contador
            {estado.listo ? (
              <Badge variant="secondary" className="gap-1">
                <CheckCircle2 size={14} /> listo
              </Badge>
            ) : (
              <Badge variant="warning">faltan {estado.faltan?.length || 0}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Qué falta, ARRIBA. Al final no lo leía nadie. */}
          {!estado.listo && !estado.empresaCreada && (
            <p className="text-body-sm text-neutral-700 dark:text-neutral-300">
              {pasoPendiente === 'archivo' && 'Falta subir el catálogo.'}
              {pasoPendiente === 'numeracion' &&
                `Falta confirmar ${gruposPendientes.length} grupo(s) de su numeración.`}
              {pasoPendiente === 'cuentas' &&
                `Falta elegir ${clavesSinConfirmar.length} cuenta(s) clave.`}{' '}
              {publico
                ? 'Con eso el asesor ya puede crear la empresa.'
                : estado.nacePorEntrega
                  ? 'Con eso ya se puede entregar la empresa: nace con este catálogo.'
                  : 'Con eso ya se puede crear la empresa.'}
            </p>
          )}
          {!publico && estado.origen === 'cotizacion' && !estado.empresaCreada && (
            <p className="text-caption text-neutral-500 dark:text-neutral-400 flex flex-wrap items-center gap-2">
              Contestado en esta cotización.
              <Button variant="ghost" size="xs" onClick={() => setRepreguntando(true)}>
                <Pencil size={12} className="mr-1" /> Cambiar
              </Button>
            </p>
          )}

          <Stepper
            pasos={PASOS.map((p) => ({ ...p, conError: false }))}
            actual={pasoVisible}
            onIr={setPaso}
            habilitados={PASOS.filter((p) => p.clave === pasoPendiente || hechos[p.clave]).map((p) => p.clave)}
          />

          {/* ── 1. Archivo ─────────────────────────────────────────────── */}
          {pasoVisible === 'archivo' && (
            <div className="space-y-3">
              <input
                ref={inputArchivo}
                type="file"
                accept=".xlsx,.xlsm,.xls,.csv,.txt,.json,.dbf"
                className="hidden"
                onChange={(ev) => subir(ev.target.files?.[0])}
              />
              <div className="flex flex-wrap items-center gap-3">
                {/* Otro archivo sobre una empresa creada mezclaría dos catálogos: el api lo rechaza. */}
                {!congelado && (
                  <Button variant={estado.archivo ? 'outline' : 'default'} disabled={subiendo} onClick={pedirArchivo}>
                    {subiendo ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Upload size={16} className="mr-1.5" />}
                    {estado.archivo ? 'Subir otro archivo' : 'Subir el catálogo (Excel o CSV)'}
                  </Button>
                )}
                {estado.archivo && (
                  <span className="text-body-sm text-neutral-600 dark:text-neutral-400">
                    <strong>{estado.archivo}</strong> — {estado.cuentas} cuentas leídas
                  </span>
                )}
              </div>
              {congelado && (
                <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
                  La empresa ya está creada con este catálogo, así que aquí ya no se sube otro archivo. {CAMBIO_EN_ESCRITORIO}
                </p>
              )}
              {estado.cuentas > 0 && (
                <Button size="sm" onClick={() => setPaso('numeracion')}>
                  Siguiente: su numeración
                </Button>
              )}
            </div>
          )}

          {/* ── 2. Numeración ──────────────────────────────────────────── */}
          {pasoVisible === 'numeracion' && estado.cuentas > 0 && (
            <div className="space-y-3">
              <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
                No hay que clasificar las {estado.cuentas} cuentas: basta con decir qué es cada grupo de su
                numeración ({grupos.length} en total).
              </p>

              {reconocidosSinConfirmar.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 rounded-md bg-neutral-50 dark:bg-neutral-800/60 px-3 py-2">
                  <span className="text-body-sm text-neutral-700 dark:text-neutral-300">
                    <strong>{reconocidosSinConfirmar.length}</strong> grupo(s) se reconocen por su nombre.
                  </span>
                  <Button
                    size="sm"
                    onClick={() =>
                      conciliar({
                        decisiones: {
                          ...decisiones,
                          ...Object.fromEntries(reconocidosSinConfirmar.map((g) => [g.codigo, g.propuesto])),
                        },
                      })
                    }
                  >
                    <CheckCircle2 size={16} className="mr-1.5" /> Confirmarlos
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setVerReconocidos((v) => !v)}>
                    {verReconocidos ? 'Ocultar' : 'Ver cuáles'}
                  </Button>
                </div>
              )}

              {dudosos.length > 0 && (
                <p className="text-body-sm font-medium text-neutral-900 dark:text-neutral-50">
                  Estos {dudosos.length} necesitan que alguien diga qué son:
                </p>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-body-sm">
                  <tbody>
                    {grupos
                      .filter((g) => verReconocidos || esDudoso(g) || decisiones[g.codigo])
                      .map((g) => (
                        <tr key={g.codigo} className="border-t border-neutral-200 dark:border-neutral-800 align-top">
                          <td className="py-2 pr-3" title={g.nombre}>
                            <span className="font-mono">{g.codigo}</span>{' '}
                            <span className="text-neutral-700 dark:text-neutral-300">{rotuloCorto(g.nombre)}</span>
                            <span className="block text-caption text-neutral-500 dark:text-neutral-400">
                              {g.cuentas} cuentas
                            </span>
                          </td>
                          <td className="py-2 pr-3">
                            <Select
                              value={decisiones[g.codigo] || SIN_ELEGIR}
                              onValueChange={(v) =>
                                conciliar({ decisiones: { ...decisiones, [g.codigo]: v === SIN_ELEGIR ? undefined : v } })
                              }
                            >
                              <SelectTrigger className="w-52 sm:w-64 max-w-full h-10 sm:h-9">
                                <SelectValue placeholder="Elegí" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={SIN_ELEGIR}>Sin confirmar</SelectItem>
                                {TIPOS.map((t) => (
                                  <SelectItem key={t.valor} value={t.valor}>
                                    {t.etiqueta}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {!decisiones[g.codigo] && g.propuesto && (
                              <p className="mt-1 text-caption text-neutral-500 dark:text-neutral-400">
                                Se propone: {etiquetaTipo(g.propuesto)}. {g.porque}
                              </p>
                            )}
                            {!decisiones[g.codigo] && !g.propuesto && g.porque && (
                              <p className="mt-1 text-caption text-neutral-500 dark:text-neutral-400">{g.porque}</p>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {gruposPendientes.length === 0 && (
                <Button size="sm" onClick={() => setPaso('cuentas')}>
                  Siguiente: cuentas clave
                </Button>
              )}
            </div>
          )}

          {/* ── 3. Cuentas clave ───────────────────────────────────────── */}
          {pasoVisible === 'cuentas' && conciliacion && (
            <div className="space-y-3">
              <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
                {clave.length} cuentas que el sistema necesita para operar desde el primer día. El resto se
                puede ajustar después, desde la empresa.
              </p>

              {propuestasDeClave.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 rounded-md bg-neutral-50 dark:bg-neutral-800/60 px-3 py-2">
                  <span className="text-body-sm text-neutral-700 dark:text-neutral-300">
                    <strong>{propuestasDeClave.length}</strong> se reconocieron por nombre.
                  </span>
                  <Button
                    size="sm"
                    onClick={() =>
                      conciliar({
                        confirmaciones: {
                          ...confirmaciones,
                          ...Object.fromEntries(propuestasDeClave.map((e) => [e.concepto, e.codigo])),
                        },
                      })
                    }
                  >
                    <CheckCircle2 size={16} className="mr-1.5" /> Aceptar las propuestas
                  </Button>
                </div>
              )}

              <div className="relative max-w-sm">
                <Search size={15} className="absolute left-2.5 top-2.5 text-neutral-400" />
                <Input
                  className="pl-8"
                  placeholder="Buscar por concepto, código o nombre"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-body-sm">
                  <tbody>{clave.filter(coincide).map(filaConcepto)}</tbody>
                </table>
              </div>

              {otros.length > 0 && (
                <details className="rounded-md border border-neutral-200 dark:border-neutral-800">
                  <summary className="cursor-pointer px-3 py-2 text-body-sm text-neutral-700 dark:text-neutral-300">
                    Otras {otros.length} cuentas (planilla, retenciones, cierre…) — opcional, para el contador
                  </summary>
                  <div className="overflow-x-auto px-3 pb-3">
                    <table className="w-full text-body-sm">
                      <tbody>{otros.filter(coincide).map(filaConcepto)}</tbody>
                    </table>
                  </div>
                </details>
              )}

              {clavesSinConfirmar.length === 0 && (
                <Button size="sm" onClick={() => setPaso('revisar')}>
                  Siguiente: revisar
                </Button>
              )}
            </div>
          )}

          {/* ── 4. Revisar ─────────────────────────────────────────────── */}
          {pasoVisible === 'revisar' && resumen && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Cifra valor={resumen.cuentas} rotulo="cuentas entran" tono="bien" />
                <Cifra valor={resumen.imputables} rotulo="admiten movimiento" />
                <Cifra valor={resumen.excluidas} rotulo="quedan fuera a propósito" tono={resumen.excluidas ? 'aviso' : 'neutral'} />
                <Cifra valor={resumen.sinTipo} rotulo="sin grupo que las clasifique" tono={resumen.sinTipo ? 'mal' : 'neutral'} />
              </div>
              <ListaCuentas titulo="Quedan fuera (grupos marcados «No importar»)" cuentas={conciliacion.excluidas} />
              <ListaCuentas titulo="No cuelgan de ningún grupo y no se pueden importar" cuentas={conciliacion.sinTipo} />

              {!estado.empresaCreada && estado.listo && (
                <Alert>
                  <AlertDescription className="flex items-center gap-1.5">
                    <CheckCircle2 size={15} />
                    {publico
                      ? 'Todo listo: el asesor ya puede crear la empresa con este catálogo. Lo guardado le llega solo.'
                      : estado.nacePorEntrega
                        ? 'Todo listo: la empresa nace con este catálogo al entregarla a producción.'
                        : 'Todo listo: ya se puede crear la empresa con este catálogo.'}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* ── Con la empresa YA creada: completarla ──────────────────── */}
          {!publico && estado.empresaCreada && hechos.numeracion && (
            <div className="space-y-3 border-t border-neutral-200 dark:border-neutral-800 pt-4">
              <h4 className="text-body font-semibold text-neutral-900 dark:text-neutral-50">La empresa ya existe</h4>
              <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
                Si quedaron cuentas del catálogo fuera de la empresa (un grupo sin confirmar al crearla, un archivo
                corregido), se agregan aquí. Las que ya están no se tocan.
              </p>
              <Button variant="outline" size="sm" disabled={completando} onClick={ensayarEmpresa}>
                {completando && <Loader2 size={14} className="mr-1.5 animate-spin" />}
                Comparar el catálogo con la empresa
              </Button>
              {completado && (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <Cifra valor={completado.resumen.faltan} rotulo="faltan en la empresa" tono={completado.resumen.faltan ? 'aviso' : 'bien'} />
                    <Cifra valor={completado.resumen.yaEstan} rotulo="ya están" />
                    <Cifra valor={completado.resumen.fuera} rotulo="fuera a propósito" />
                    <Cifra valor={completado.resumen.conError} rotulo="no entran" tono={completado.resumen.conError ? 'mal' : 'neutral'} />
                  </div>
                  <ListaCuentas titulo="Se agregarían" cuentas={completado.faltan} />
                  <ListaCuentas titulo="No entran" cuentas={completado.conError} />
                  {/* El umbral se mide sobre el catálogo ENTERO: un catálogo con más del 5 % de filas
                      malas no se «completa» mandando lo poco que falta. */}
                  {completado.resumen.superaUmbral ? (
                    <Alert variant="destructive">
                      <AlertDescription>
                        Más del 5 % de las filas del catálogo no entran, así que no se completa: quedaría un plan a
                        medias. Revisa los grupos de su numeración (paso 2) o reemplázalo desde el escritorio.
                      </AlertDescription>
                    </Alert>
                  ) : completado.resumen.faltan > 0 || estado.carga?.ok !== true ? (
                    <Button size="sm" disabled={completando} onClick={() => setConfirmarEscribir(true)}>
                      {completado.resumen.faltan > 0
                        ? `Agregar ${completado.resumen.faltan} cuenta(s) a la empresa`
                        : 'Fijar las cuentas clave y cerrar el catálogo'}
                    </Button>
                  ) : (
                    <p className="text-body-sm text-success-700 dark:text-success-300 flex items-center gap-1.5">
                      <CheckCircle2 size={15} /> La empresa tiene todo el catálogo.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Cómo quedó en la empresa: aplicado, cerrado o abierto, y por qué. Si no entró
              entero, es lo que bloquea la entrega. */}
          {!publico && estado.empresaCreada && <EstadoEnLaEmpresa carga={estado.carga} />}
        </CardContent>
      </Card>

      {!publico && <EnlaceContador propuestaId={propuestaId} enlace={estado.enlace} onEmitido={refrescar} />}

      {/* ── La firma, aparte ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-body font-semibold flex items-center gap-2">
            Firma del catálogo
            {estado.contador ? (
              <Badge variant="secondary">completa</Badge>
            ) : (
              <Badge variant="outline">pendiente</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
            Quién autorizó este catálogo. No frena la importación: sin estos datos las cuentas entran igual, pero el
            catálogo no queda cerrado como el sistema contable autorizado del cliente.
            {estado.empresaCreada && ' Con la empresa ya creada, guardarla completa lo cierra.'}
          </p>
          <CamposContador
            contador={contador}
            onCambio={setContador}
            completo={Boolean(estado.contador)}
            pie={
              <Button variant="outline" size="sm" disabled={guardandoContador} onClick={() => declarar(true, contador)}>
                {guardandoContador && <Loader2 size={14} className="mr-1.5 animate-spin" />}
                Guardar
              </Button>
            }
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        abierto={confirmarReemplazo}
        onCerrar={() => setConfirmarReemplazo(false)}
        onConfirmar={() => {
          setConfirmarReemplazo(false)
          inputArchivo.current?.click()
        }}
        titulo="¿Subir otro catálogo?"
        descripcion="Se borra lo que ya confirmaste sobre el archivo actual —qué es cada grupo y qué cuenta es cada concepto— y hay que volver a revisarlo con el nuevo."
        textoConfirmar="Elegir el archivo nuevo"
        variante="destructiva"
      />

      <ConfirmDialog
        abierto={confirmarEscribir}
        onCerrar={() => setConfirmarEscribir(false)}
        onConfirmar={escribirFaltantes}
        titulo={
          completado?.resumen?.faltan
            ? `¿Agregar ${completado.resumen.faltan} cuenta(s) a la empresa?`
            : '¿Fijar las cuentas clave y cerrar el catálogo?'
        }
        descripcion="Las cuentas que faltan se escriben como una carga que se puede deshacer desde «Datos de prueba»; las que ya están no se modifican. Si la carga del alta no había terminado, se fijan también las cuentas clave confirmadas y, con la firma completa, el catálogo se cierra como el sistema contable autorizado."
        textoConfirmar="Continuar"
        cargando={completando}
      />
    </div>
  )
}
